import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/ui/BackButton";
import { CaretRight, CaretLeft } from "@phosphor-icons/react";

type EditionAyah = { number: number; text: string; numberInSurah: number };
type SurahPayload = {
  data: {
    number: number;
    name: string;
    englishName: string;
    englishNameTranslation: string;
    numberOfAyahs: number;
    revelationType: string;
    ayahs: EditionAyah[];
  };
};

const PAGE_FONT_KEY = "surah_view_font_size";
const DEFAULT_SIZE = 35;

export default function SurahViewPage() {
  const { id } = useParams();
  const surahNumber = Math.min(114, Math.max(1, Number(id ?? 1)));
  return <SurahViewClient surahNumber={surahNumber} />;
}

function SurahViewClient({ surahNumber }: { surahNumber: number }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [surah, setSurah] = useState<SurahPayload["data"] | null>(null);
  const [fontSize, setFontSize] = useState<number>(DEFAULT_SIZE);
  const firstLoad = useRef(true);
  const [dir, setDir] = useState<"rtl" | "ltr">("ltr");

  useEffect(() => {
    setDir(document.documentElement.dir as "rtl" | "ltr");
  }, []);

  // ✅ document title (replaces Next generateMetadata)
  useEffect(() => {
    let cancelled = false;
    const fallback = `${surahNumber} | نور على نور`;
    document.title = fallback;
    fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const name = d?.data?.name;
        if (name) document.title = `${name} | نور على نور`;
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [surahNumber]);

  // fetch Uthmani text
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}/quran-uthmani`)
      .then((r) => r.json())
      .then((json: SurahPayload) => {
        if (!mounted) return;
        setSurah(json.data);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [surahNumber]);

  useEffect(() => {
    if (!firstLoad.current) return;
    firstLoad.current = false;
    const saved = localStorage.getItem(PAGE_FONT_KEY);
    if (saved) {
      const n = Number(saved);
      if (!Number.isNaN(n)) setFontSize(n);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem(PAGE_FONT_KEY, String(fontSize));
  }, [fontSize]);

  const isTawbah = surahNumber === 9;
  const cleanedAyahs = useMemo(() => {
    if (!surah) return [];
    return surah.ayahs.map((a) => {
      if (a.numberInSurah === 1 && !isTawbah) {
        const stripped = a.text
          .replace("بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ", "")
          .trim();
        return { ...a, text: stripped };
      }
      return a;
    });
  }, [surah, isTawbah]);

  const onPrev = () => {
    if (surahNumber > 1) navigate(`/surah/${surahNumber - 1}`);
  };
  const onNext = () => {
    if (surahNumber < 114) navigate(`/surah/${surahNumber + 1}`);
  };

  return (
    <Container>
      <TopBar>
        <BackButton href="/surah" label="رجوع إلى فهرس السور" />
        <TopTitle>{surah?.name ?? "المصحف الشريف"}</TopTitle>
        <Spacer />
      </TopBar>

      <Card>
        <ControlBar>
          <Label>حجم الخط</Label>
          <Range
            min={18}
            max={52}
            step={1}
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            aria-label="تغيير حجم الخط"
          />
          <SizeBadge>{fontSize}</SizeBadge>
        </ControlBar>

        {loading || !surah ? (
          <Loader>جارٍ التحميل...</Loader>
        ) : (
          <ScrollArea>
            {!isTawbah && cleanedAyahs.length > 0 && <Basmala>﷽</Basmala>}
            <AyahText $size={fontSize}>
              {cleanedAyahs.map((a) => (
                <span key={a.number}>
                  {a.text}{" "}
                  <AyahNumber $size={Math.round(fontSize * 0.75)}>
                    ﴿{a.numberInSurah}﴾{" "}
                  </AyahNumber>
                </span>
              ))}
            </AyahText>
          </ScrollArea>
        )}
      </Card>

      <Footer>
        <PageNote>السورة {surahNumber} من 114</PageNote>
        <NavButtons $rtl={dir === "rtl"}>
          <NavBtn
            $primary
            $flip={dir === "rtl"}
            onClick={onNext}
            disabled={surahNumber >= 114 || loading}
          >
            {dir === "rtl" ? (
              <CaretLeft size={18} weight="bold" />
            ) : (
              <CaretRight size={18} weight="bold" />
            )}{" "}
            السورة التالية
          </NavBtn>
          <NavBtn
            $primary
            $flip={dir !== "rtl"}
            onClick={onPrev}
            disabled={surahNumber <= 1 || loading}
          >
            {dir === "rtl" ? (
              <CaretRight size={18} weight="bold" />
            ) : (
              <CaretLeft size={18} weight="bold" />
            )}{" "}
            السورة السابقة
          </NavBtn>
        </NavButtons>
      </Footer>
    </Container>
  );
}

/* styles */
const Container = styled.div`
  padding-top: 20px;
  padding-inline: clamp(12px, 4vw, 28px);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;
const TopBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-bottom: 8px;
  width: 100%;
`;

const TopTitle = styled.h2`
  margin: 0;
  text-align: center;
  font-family: "Lateef", "Scheherazade", system-ui;
  font-size: 3em;
  font-weight: 500;
  color: var(--primary);
`;
const Spacer = styled.div``;
const Card = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 16px;
  margin: 12px auto 20px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 960px;
  box-sizing: border-box;
`;
const ControlBar = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
  background: var(--surface);
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid var(--border);
`;
const Label = styled.span`
  color: var(--primary);
  font-weight: 600;
  font-size: 0.95rem;
`;
const Range = styled.input.attrs({ type: "range" })`
  width: 100%;
  accent-color: var(--primary);
`;
const SizeBadge = styled.span`
  min-width: 40px;
  text-align: center;
  color: var(--primary);
  font-weight: 700;
`;
const Loader = styled.div`
  text-align: center;
  padding: 40px 10px;
  font-size: 1.05rem;
`;
const ScrollArea = styled.div`
  margin-top: 12px;
  max-height: min(70vh, 640px);
  overflow: auto;
  padding-right: 6px;
  max-width: 100%;
  overflow-x: hidden;
`;
const Basmala = styled.div`
  text-align: center;
  color: var(--primary);
  font-size: clamp(2rem, 6vw, 3.2rem);
  font-family: "Lateef", "Scheherazade", system-ui;
  margin: 8px 0 16px;
`;
const AyahText = styled.p<{ $size: number }>`
  direction: rtl;
  text-align: center;
  color: var(--text);
  font-family: "Lateef", "Scheherazade", system-ui;
  font-size: ${(p) => p.$size}px;
  line-height: ${(p) => Math.round(p.$size * 1.6)}px;
  margin: 0;
  transition: font-size 140ms ease, line-height 140ms ease;
  word-spacing: 2px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  span {
    white-space: normal;
  }
`;
const AyahNumber = styled.span<{ $size: number }>`
  color: var(--primary);
  font-size: ${(p) => p.$size}px;
`;
const Footer = styled.div`
  padding: 0 8px 22px;
`;
const PageNote = styled.div`
  text-align: center;
  color: var(--text);
  margin-bottom: 12px;
  font-size: 0.95rem;
`;
const NavButtons = styled.div<{ $rtl?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$rtl ? "row-reverse" : "row")};
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  @media (max-width: 520px) {
    flex-direction: column;
    align-items: center;
  }
`;
const NavBtn = styled.button<{ $primary?: boolean; $flip?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-direction: ${(p) => (p.$flip ? "row-reverse" : "row")};
  padding: 14px 12px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.98rem;
  background: ${(p) => (p.$primary ? "var(--primary)" : "var(--surface)")};
  color: ${(p) => (p.$primary ? "#fff" : "var(--text)")};
  box-shadow: ${(p) =>
    p.$primary ? "0 8px 18px rgba(0,0,0,.14)" : "var(--shadow)"};
  transition: transform 120ms ease, filter 120ms ease, opacity 120ms ease;
  width: clamp(220px, 40vw, 420px);
  @media (max-width: 520px) {
    width: 100%;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    transform: translateY(-2px);
    filter: brightness(1.02);
  }
`;
